<template>
    <v-container fluid>
        <v-row>
            <v-col>
                <h4 class="text-h4">Supplier Account Statement</h4>
                <p class="text-subtitle-1">Statement of Account for Supplier Subledger</p>
                <v-divider></v-divider>
            </v-col>
        </v-row>
        <v-form ref="form">
            <v-row>
                <v-col cols="12" md="4">
                    <v-autocomplete
                        label="Supplier"
                        :value="supplier_id"
                        @input="inputSupplierID"
                        :items="suppliers"
                        item-text="display_name"
                        @update:search-input="searchSuppliers"
                        @change="handleSupplierChange"
                        :rules="[isRequired]"
                        item-value="id"
                        outlined
                        dense
                        hide-details
                        >
                    </v-autocomplete>
                </v-col>
                <v-col cols="12" md="2">
                    <v-autocomplete
                        label="Account"
                        :value="account_id"
                        @input="inputAccountID"
                        :items="accounts"
                        :rules="[isRequired]"
                        item-text="display_name"
                        item-value="id"
                        outlined
                        dense
                        hide-details="auto"
                        >
                    </v-autocomplete>
                </v-col>
                <v-col cols="12" md="2">
                    <DatePicker
                        label="from"
                        :value="from"
                        @input="inputFrom"
                        :rules="[isRequired]"
                        outlined
                        dense
                        hide-details/>
                </v-col>
                <v-col cols="12" md="2">
                    <DatePicker
                        label="To"
                        :value="to"
                        @input="inputTo"
                        outlined
                        :rules="[isRequired]"
                        dense
                        hide-details/>
                </v-col>
                <v-col cols="12" md="1">
                    <v-btn block color="primary" @click="validateAndFetch" :loading="loading">
                        <v-icon left>mdi-magnify</v-icon>
                        search
                    </v-btn>
                </v-col>
                <v-col cols="12" md="1">
                    <v-btn icon depressed @click="print" :loading="printLoading">
                        <v-icon>mdi-printer</v-icon>
                    </v-btn>
                </v-col>
            </v-row>
        </v-form>
        <v-row>
            <v-col>
                <v-progress-linear indeterminate v-if="loading"/>
                <v-simple-table dense>
                    <thead>
                        <tr>
                        <th width="100px"><b>Date</b></th>
                            <th width="120px"><b>Trans #</b></th>
                            <th><b>Narration</b></th>
                            <th><b>Debit</b></th>
                            <th><b>Credit</b></th>
                            <th><b>Balance</b></th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr class="font-weight-bold">
                            <td colspan="5">Opening balance</td>
                            <td>
                                <span :class="{'error--text': isNegative(data && data.statement && data.statement.opening ? data.statement.opening.balance: null)}">
                                        {{formatMoney(data.statement.opening.balance)}}
                                </span>
                            </td>
                        </tr>
                        <tr v-for="(item,i) in data.statement.running" :key="i" v-ripple @dblclick="handleRowDbClick(item)">
                            <td>{{item.date}}</td>
                            <td>
                                <span class="font-weight-bold text-decoration-underline" style="cursor: pointer" @click="flashback(item)">{{item.reference_code}}</span>
                            </td>
                            <td>{{item.narration}}</td>
                            <td>{{formatNumber(debit(item.amount))}}</td>
                            <td>{{formatNumber(credit(item.amount))}}</td>
                            <td style="width: 180px">
                                <span :class="{'error--text': isNegative(item.balance)}">
                                    {{formatMoney(item.balance)}}
                                </span>
                            </td>
                        </tr>
                        <tr class="font-weight-bold">
                            <td colspan="3">Closing balance</td>
                            <td>{{formatNumber(totalDebit)}}</td>
                            <td>{{formatNumber(totalCredit)}}</td>
                            <td>
                                <span :class="{'error--text': isNegative(data.statement.closing.balance)}">
                                    {{formatMoney(data.statement.closing.balance)}}
                                </span>
                            </td>
                        </tr>
                    </tbody>
                </v-simple-table>
            </v-col>
        </v-row>
    </v-container>
</template>

<script>
import DatePicker from '../../components/common/DatePicker.vue';
import { formatMoney, formatNumber } from 'accounting';
import { getTotal } from './../../utils/accounting'
import { mapActions, mapState } from 'vuex';
import { isRequired } from './../../utils/validations';
import API from './../../services/purchase/supplierAccountStatement'

export default {
    components: {
        DatePicker
    },
    data() {
        return {
            printLoading: false
        }
    },
    computed: {
        ...mapState("purchase/supplierAccountStatement", [
            "suppliers",
            "suppliersLoading",
            "supplier_id",
            "account_id",
            "accounts",
            "from",
            "to",
            "loading",
            "data"
        ]),
        totalDebit() {
            if (!this.data) return null;
            let debit = this.data.statement.running.filter(each => each.amount > 0);
            return getTotal(debit, "amount");
        },
        totalCredit() {
            if(!this.data) return null;
            let credit = this.data.statement.running.filter(each => each.amount < 0);
            return Math.abs(getTotal(credit, "amount"));
        }
    },
    methods: {
        getTotal,
        isRequired,
        ...mapActions("purchase/supplierAccountStatement", [
            "searchSuppliers",
            "inputSupplierID",
            "inputAccountID",
            "inputFrom",
            "inputTo",
            "fetch",
            "reset"
        ]),
        validateAndFetch() {
            if(!this.$refs.form.validate()) return;
            this.fetch();
        },
        debit(amount) {
            if(Math.sign(amount) == 1) return amount;
            return 0;
        },
        credit(amount) {
            if(Math.sign(amount) == -1) return Math.abs(amount);
            return 0;
        },
        formatMoney(n) {
            return formatMoney(n, {
                symbol: this.data.account.currency.abbreviation,
                precision: 2,
                thousand: ",",
                format: {
                    pos : "%s %v",
                    neg : "%s (%v)",
                    zero: "--"
                }
            });
        },
        formatNumber(n) {
            return formatNumber(n, 2)
        },
        isNegative(n) {
            return Math.sign(n) == -1;
        },
        flashback(row) {
            switch(row.reference_type) {
                case "INV":
                    this.$router.push({ name: "PurchaseInvoiceDisplay", params: { id: row.reference_id } });
                    break;
                case "PMT":
                    this.$router.push({ name: "PurchasePaymentDisplay", params: { id: row.reference_id } });
                    break;
                case "CN":
                    this.$router.push({ name: "CreditNoteDisplay", params: { id: row.reference_id } });
                    break;
                case "DN":
                    this.$router.push({ name: "DebitNoteDisplay", params: { id: row.reference_id } });
                    break;
                // TODO: need to fix the jv opening for moved account before implementing the flashback
                // case "JV":
                //     this.$router.push({ name: "JournalEntryDisplay", params: { id: row.reference_id } });
                //     break;
                default:
                    alert("document not supported in flashback")
            }
        },
        handleSupplierChange() {
            this.inputAccountID(null)
            this.reset();
        },
        handleRowDbClick(item) {
            this.flashback(item)
        },
        print() {
            this.printLoading = true;
            API.print({
                supplier_id: this.supplier_id,
                account_id: this.account_id,
                from: this.from,
                to: this.to
            })
                .then(response => {
                    var printWindow = window.open('','','width=800,height=600');
                    printWindow.document.write(response.data);
                    printWindow.document.close();
                    printWindow.focus();
                    printWindow.print();
                    printWindow.close();
                })
                .finally(() => {
                    this.printLoading = false;
                })
        }
    }
}
</script>

<style>

</style>